import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import '../Reports.css';
import { abbreviateNumber } from '../ReportsHelper';
import { MetricCardTooltips } from '../ReportTooltips';
import { ClientConfigurationContext } from '../../../context/ClientConfigurationContextProvider';

const PERCENTAGE_METRICS = new Set([
    'Conversion',
    'LossRatio',
    'AttritionThirtyDay',
    'RetentionHalfYear',
    'RetentionFullYear',
    'PercentNSF',
    'PercentRecurringPayment'
]);

const MONEY_METRICS = new Set([
    'BoundPremium',
    'EarnedPremium',
    'WrittenPremium',
    'NewBusinessWrittenPremium',
    'TotalAmountPaid',
    'AverageAmountPaid'
]);

const TITLE_MAPPINGS = {
    AttritionThirtyDay: "30 Day Attrition",
    RetentionHalfYear: "184 Day Retention",
    RetentionFullYear: "365 Day Retention",
    Conversion: "Conversion (Closure)",
    TotalPif: "Total PIF",
    BoundPremium: "New Business Written Premium",
    PercentNSF: "Percent NSF",
    WrittenPremium: "Total Written Premium"
};

export default function MetricCard({ number, title }) {
    const clientConfiguration = useContext(ClientConfigurationContext);

    const metricData = useMemo(() => {
        const isPercentage = PERCENTAGE_METRICS.has(title);
        const isMoney = MONEY_METRICS.has(title);

        const formattedTitle = TITLE_MAPPINGS[title] ||
            title.split(/(?=[A-Z])/).join(' ').trim();

        const tooltipContent = MetricCardTooltips(formattedTitle);

        const formatValue = (abbreviated = false) => {
            if (number === null) return "N/A";

            if (isMoney) {
                return abbreviated ? `$${abbreviateNumber(number)}` : `$${number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            }

            if (isPercentage) {
                const percentage = number * 100;
                return abbreviated ? `${abbreviateNumber(percentage)}%` : `${percentage.toLocaleString()}%`;
            }

            return abbreviated ? abbreviateNumber(number) : number.toLocaleString();
        };

        return {
            formattedTitle,
            tooltipContent,
            abbreviatedValue: formatValue(true),
            extendedValue: formatValue(false)
        };
    }, [number, title]);

    if (clientConfiguration && !clientConfiguration.showLossRatioInReports && title === "LossRatio") {
        return null;
    }

    return (
        <div className="card">
            <div className="tooltip-label-container header">
                <div>{metricData.formattedTitle}</div>
                {metricData.tooltipContent && <gds-tooltip>{metricData.tooltipContent}</gds-tooltip>}
            </div>
            <div className="number">
                <span className="abbreviated">{metricData.abbreviatedValue}</span>
                <span className="extended">{metricData.extendedValue}</span>
            </div>
        </div>
    );
}

MetricCard.propTypes = {
    number: PropTypes.number,
    title: PropTypes.string,
};